/**
 * Multi-tab coordination via BroadcastChannel.
 *
 * Detects when multiple tabs are editing the same project and warns.
 * Uses last-save-wins strategy — no locking, just awareness.
 *
 * Also coordinates FFmpeg transcoding to prevent duplicate work:
 * before starting a transcode, a tab claims it via broadcast.
 * Other tabs seeing the same assetId skip their own transcode.
 */

import toast from 'react-hot-toast';

const CHANNEL_NAME = 'vidnight-tab-sync';
const TAB_ID = crypto.randomUUID();

interface TabMessage {
  type: 'project-open' | 'project-save' | 'transcode-claim' | 'tab-ping' | 'tab-pong';
  tabId: string;
  projectId?: string;
  assetId?: string;
  timestamp: number;
}

let channel: BroadcastChannel | null = null;
let currentProjectId: string | null = null;
let warningShown = false;
const activeTranscodes = new Set<string>();

function getChannel(): BroadcastChannel {
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = handleMessage;
  }
  return channel;
}

function handleMessage(e: MessageEvent<TabMessage>) {
  const msg = e.data;
  if (msg.tabId === TAB_ID) return; // ignore own messages

  switch (msg.type) {
    case 'project-open':
      if (msg.projectId && msg.projectId === currentProjectId && !warningShown) {
        warningShown = true;
        toast(
          'This project is open in another tab. Changes may overwrite each other.',
          { duration: 6000, icon: '⚠️' },
        );
      }
      break;

    case 'project-save':
      if (msg.projectId && msg.projectId === currentProjectId && !warningShown) {
        warningShown = true;
        toast(
          'This project was saved from another tab.',
          { duration: 4000, icon: '⚠️' },
        );
      }
      break;

    case 'transcode-claim':
      if (msg.assetId) {
        activeTranscodes.add(msg.assetId);
      }
      break;

    case 'tab-ping':
      // Respond with pong so the other tab knows we're here
      if (msg.projectId === currentProjectId) {
        getChannel().postMessage({
          type: 'tab-pong',
          tabId: TAB_ID,
          projectId: currentProjectId,
          timestamp: Date.now(),
        } satisfies TabMessage);
      }
      break;
  }
}

/** Announce that this tab is editing a project */
export function announceProjectOpen(projectId: string) {
  currentProjectId = projectId;
  warningShown = false;
  getChannel().postMessage({
    type: 'project-open',
    tabId: TAB_ID,
    projectId,
    timestamp: Date.now(),
  } satisfies TabMessage);
}

/** Announce that this tab saved the project */
export function announceProjectSave(projectId: string) {
  getChannel().postMessage({
    type: 'project-save',
    tabId: TAB_ID,
    projectId,
    timestamp: Date.now(),
  } satisfies TabMessage);
}

/** Claim a transcode job. Returns true if no other tab is already transcoding this asset. */
export function claimTranscode(assetId: string): boolean {
  if (activeTranscodes.has(assetId)) {
    return false; // another tab is already transcoding
  }
  getChannel().postMessage({
    type: 'transcode-claim',
    tabId: TAB_ID,
    assetId,
    timestamp: Date.now(),
  } satisfies TabMessage);
  return true;
}

/** Release a transcode claim when done */
export function releaseTranscode(assetId: string) {
  activeTranscodes.delete(assetId);
}

/** Clean up on unmount */
export function closeTabSync() {
  channel?.close();
  channel = null;
  currentProjectId = null;
}
